import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Collapse,
} from "reactstrap";
import Select from "react-select";
import BreadCrumb from "../../../../Components/Common/BreadCrumb";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { getLedgers } from "../../../../helpers/urlConfig/apiFunctions";
import LedgersCreate from "./LedgersCreate";
import { useNavigate } from "react-router-dom";
import { RiAddLine } from "react-icons/ri";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import DeleteComponent from "../../../../Components/Common/deleteComponent/DeleteComponent";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { documentTitle } from "../../../../helpers/documentTitle";
import errorResponseHandler from "../../../../helpers/errorResponseHandler";
import { dateChangerFunc } from "../../../../helpers/dateParser";

const LedgersList = () => {
  const [modal_list, setmodal_list] = useState(false);
  const [ledgers, setLedgers] = useState([]);
  const [filteredLedgers, setFilteredLedgers] = useState([]);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [openingBalance, setOpeningBalance] = useState(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const navigate = useNavigate();

  const tog_list = () => setmodal_list(!modal_list);
  const toggleFilter = () => setFilterOpen(!filterOpen);

  const [modal_delete, setmodal_delete] = useState(false);
  const [deleteId, setDeleteCustomerId] = useState(null);
  const [deleteName, setDeleteCustomerName] = useState(null);

  const tog_delete = () => setmodal_delete(!modal_delete);

  documentTitle("Customer Balance");

  const fetchData = async () => {
    try {
      const response = await getLedgers();

      if (response.statusCode === 200) {
        setLedgers(response.data);
        setFilteredLedgers(response.data);
        setOpeningBalance(response.total_opening_balance);
        setCustomerOptions(
          response.data.map((user) => ({
            value: user.customer_name,
            label: user.customer_name,
          }))
        );
      } else {
        errorResponseHandler(response);
      }
    } catch (error) {
      console.error(error.message);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filterData = () => {
    let filtered = ledgers;

    if (startDate && endDate) {
      filtered = filtered.filter((ledger) => {
        const createdAt = new Date(ledger.created_at);
        // Check if createdAt is between or equal to startDate and endDate
        return (
          createdAt >= startDate.setHours(0, 0, 0, 0) &&
          createdAt <= endDate.setHours(23, 59, 59, 999)
        );
      });
    }

    if (selectedCustomer) {
      filtered = filtered.filter(
        (ledger) => ledger.customer_name === selectedCustomer.value
      );
    }

    setFilteredLedgers(filtered);
  };

  const handleDateFilter = () => {
    filterData(); // Apply date filter and customer filter simultaneously
  };

  const handleCustomerChange = (selectedOption) => {
    setSelectedCustomer(selectedOption);
    filterData(); // Immediately apply filter when a customer is selected
  };

  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(filteredLedgers);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Ledgers");
    XLSX.writeFile(workbook, "Ledgers.xlsx");
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    doc.text("Ledgers List", 14, 10);
    doc.autoTable({
      head: [
        [
          "SL.No",
          "Customer Name",
          "Total Debit",
          "Total Credit",
          "Total Openings",
          "Created At",
        ],
      ],
      body: filteredLedgers.map((ledger, index) => [
        index + 1,
        ledger.customer_name,
        ledger.total_debit,
        ledger.total_credit,
        ledger.opening_amount,
        ledger.created_at,
      ]),
    });
    doc.save("Ledgers.pdf");
  };

  const navigateToLedgers = (ledger) => {
    const customerID = ledger.customer_id;
    navigate(`/ledgers/${customerID}`);
  };

  const onClickRemoveBtn = (ledger) => {
    setDeleteCustomerId(ledger.customer_id);
    setDeleteCustomerName("CustomerID");
    tog_delete();
  };

  const clearDateFilters = () => {
    setStartDate(null);
    setEndDate(null);
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title="Customer Balance List"
            pageTitle="Customer Balance"
            exportToExcel={exportToExcel}
            exportToPDF={exportToPDF}
          />
          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <Row className="g-2 mb-2">
                    <Col md={2}>
                      <Button
                        color="success"
                        className="me-1"
                        onClick={tog_list}
                        id="create-btn"
                      >
                        <RiAddLine className="align-bottom" />
                        <span className="d-none d-md-inline ms-1">Add</span>
                      </Button>
                    </Col>
                    <Col md={2}>
                      <Select
                        options={customerOptions}
                        isClearable
                        onChange={handleCustomerChange}
                        placeholder="Search Customer..."
                        className="flex-grow-1"
                      />
                    </Col>
                    <Col md={2}>
                      <DatePicker
                        selected={startDate}
                        onChange={(date) => setStartDate(date)}
                        selectsStart
                        startDate={startDate}
                        endDate={endDate}
                        placeholderText="From Date"
                        className="form-control"
                      />
                    </Col>
                    <Col md={2}>
                      <DatePicker
                        selected={endDate}
                        onChange={(date) => setEndDate(date)}
                        selectsEnd
                        startDate={startDate}
                        endDate={endDate}
                        minDate={startDate}
                        placeholderText="To Date"
                        className="form-control"
                      />
                    </Col>
                    <Col md={3}>
                      <Button
                        color="primary"
                        onClick={handleDateFilter}
                        className="w-sm-auto w-100"
                      >
                        Apply
                      </Button>
                    </Col>
                    <Col md={1}>
                      <Button
                        color="secondary"
                        onClick={clearDateFilters}
                        disabled={!startDate && !endDate}
                      >
                        Clear
                      </Button>
                    </Col>
                  </Row>

                  {/* Table Section */}
                  <div className="table-responsive table-card mt-3 mb-1">
                    <table className="table align-middle table-nowrap">
                      <thead className="table-light">
                        <tr>
                          <th>SL.No</th>
                          <th>Customer Name</th>
                          <th>Total Debit</th>
                          <th>Total Credit</th>
                          <th>Total Openings</th>
                          <th>Created At</th>
                          {/* <th>Action</th> */}
                        </tr>
                      </thead>
                      <tbody>
                        {filteredLedgers.map((ledger, index) => (
                          <tr key={index}>
                            <td>{index + 1}</td>
                            <td>
                              <button
                                className="btn btn-link"
                                onClick={() => navigateToLedgers(ledger)}
                              >
                                {ledger.customer_name}
                              </button>
                            </td>
                            <td>{ledger.total_debit}</td>
                            <td>{ledger.total_credit}</td>
                            <td>{ledger.opening_amount}</td>
                            <td>{dateChangerFunc(ledger.created_at)}</td>
                            <td>
                              <div className="d-flex gap-2"></div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {filteredLedgers.length === 0 && (
                      <div className="text-center mt-4">
                        <p>No Ledgers found.</p>
                      </div>
                    )}
                  </div>
                  <Col>
                    <p>
                      <strong>Total Opening Balance:</strong>{" "}
                      {openingBalance || 0}
                    </p>
                  </Col>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
      <LedgersCreate
        modal_list={modal_list}
        setmodal_list={setmodal_list}
        tog_list={tog_list}
        fetchData={fetchData}
      />

      <DeleteComponent
        modal_delete={modal_delete}
        tog_delete={tog_delete}
        fetchData={fetchData}
        deleteId={deleteId}
        keyName="Customer Ledger"
        deleteName={deleteName}
      />

      <ToastContainer />
    </React.Fragment>
  );
};

export default LedgersList;
